import { formatINR } from "@templeos/shared";
import { Alert, Badge, Button, Modal, StatCard, useToast } from "@templeos/ui";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useCheckout } from "../components/checkout.js";
import { QrImage } from "../components/PrintArea.js";
import { Shell } from "../components/Shell.js";
import { CashAlert, ShiftDrawer, useShiftTally } from "../components/ShiftPanel.js";
import { ReprintModal } from "../components/SupervisorActions.js";
import { addItem, cartTotal, CartError } from "../lib/cart.js";
import { openCustomerDisplay, postDisplay } from "../lib/display.js";
import { buildPayments, upiLink } from "../lib/payments.js";
import { pendingByItem, randomSuffix } from "../lib/queue.js";
import { billProblem, quotaBreach } from "../lib/rules.js";
import { FAST_KEYS, type FastAction } from "../lib/shortcuts.js";
import type { PosItem, QueuedSale } from "../lib/types.js";
import { useSession } from "../session.js";

interface Tap { item: PosItem; label: string; qty: number }

/**
 * SOW Module 2: festival-day fast checkout. One touch on a preset issues and prints (cash); a typed number first
 * issues that many tickets in one bill and prints them as a batch. UPI shows the QR and prints on confirmation.
 */
export function Fast() {
  const s = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const checkout = useCheckout();
  const { sales, t, drawer, quotaUsed } = useShiftTally();
  const [mult, setMult] = useState("");
  const [mode, setMode] = useState<"cash" | "upi">("cash");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // one ticket per touch, even on a double tap
  const [last, setLast] = useState<{ tap: Tap; sale: QueuedSale } | null>(null);
  const [upiTap, setUpiTap] = useState<{ tap: Tap; ref: string } | null>(null);
  const [reprint, setReprint] = useState<{ receiptId: string; local?: QueuedSale } | null>(null);
  const [shiftOpen, setShiftOpen] = useState(false);

  const presets = useMemo<{ item: PosItem; label: string; qty: number }[]>(() => {
    const items = s.config?.items ?? [];
    const configured = (s.config?.presets ?? []).flatMap((p) => { const item = items.find((i) => i.id === p.itemId); return item ? [{ item, label: p.label, qty: p.qty }] : []; });
    return (configured.length ? configured : items.filter((i) => i.kind === "ticket").map((item) => ({ item, label: item.name, qty: 1 }))).slice(0, 4);
  }, [s.config]);

  const issue = useCallback(async (tap: Tap, payMode: "cash" | "upi", ref: string) => {
    if (inFlight.current) return;
    let cart;
    try {
      cart = addItem([], tap.item, tap.qty);
    } catch (e) {
      if (e instanceof CartError) { toast(e.message, "danger"); return; }
      throw e;
    }
    const quota = quotaBreach(cart, s.config?.items ?? [], pendingByItem(sales), s.config?.cashierQuota ?? null, quotaUsed);
    if (quota) { toast(quota, "danger"); return; }
    const tax = billProblem({ payer: {}, donation: 0, cash: payMode === "cash" ? cartTotal(cart) : 0 }, s.limits);
    if (tax) { toast(tax, "danger"); return; }
    inFlight.current = true;
    setBusy(true);
    try {
      const sale = await checkout({ cart, payments: buildPayments(cartTotal(cart), payMode, null, ref) });
      if (sale && sale.status !== "rejected") { setLast({ tap, sale }); setMult(""); }
    } catch (e) {
      toast(e instanceof Error ? e.message : "The ticket could not be issued.", "danger");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [checkout, s.config, s.limits, sales, quotaUsed, toast]);

  const press = (i: number) => {
    const p = presets[i];
    if (!p) return;
    const tap = { ...p, qty: p.qty * Math.max(1, Number(mult || "1")) };
    if (mode === "upi" && tap.item.price > 0) setUpiTap({ tap, ref: `UPI${randomSuffix(8)}` });
    else void issue(tap, "cash", "");
  };

  // Digits typed on the numeric keypad set how many tickets the next touch issues.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (document.querySelector(".tos-overlay") || (e.target as HTMLElement).closest("input, select, textarea")) return;
      if (/^[0-9]$/.test(e.key)) setMult((m) => (m + e.key).replace(/^0+/, "").slice(0, 3));
      else if (e.key === "Backspace") setMult((m) => m.slice(0, -1));
      else if (e.key === "Escape") setMult("");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const upiAmount = upiTap ? upiTap.tap.item.price * upiTap.tap.qty : 0;
  let upi: string | null = null;
  let upiErr: string | null = null;
  if (upiTap) {
    try { upi = s.config?.upiVpa ? upiLink({ vpa: s.config.upiVpa, payee: s.slip.templeName, amount: upiAmount, ref: upiTap.ref }) : null; } catch (e) { upiErr = (e as Error).message; }
    if (!s.config?.upiVpa) upiErr = "UPI ID not configured for this counter; use the printed UPI standee.";
  }
  // The devotee sees the QR only while it is on the cashier's screen: cancelled, confirmed or left, it goes away.
  useEffect(() => {
    if (!upiTap || !upi) return;
    postDisplay({ type: "cart", templeName: s.slip.templeName, lines: [{ name: upiTap.tap.item.name, qty: upiTap.tap.qty, amount: upiAmount }], total: upiAmount, upi: { link: upi, amount: upiAmount } });
    return () => postDisplay({ type: "idle", templeName: s.slip.templeName });
  }, [upiTap, upi, upiAmount, s.slip.templeName]);

  const lastSynced = [...sales].reverse().find((x) => x.receiptId && !x.voided);
  const onKey = (a: FastAction) => {
    const presetIndex = { preset1: 0, preset2: 1, preset3: 2, preset4: 3 } as Partial<Record<FastAction, number>>;
    if (presetIndex[a] !== undefined) return press(presetIndex[a]!);
    switch (a) {
      case "toggleUpi": setMode((m) => (m === "cash" ? "upi" : "cash")); break;
      case "repeatLast":
        if (last) { if (mode === "upi" && last.tap.item.price > 0) setUpiTap({ tap: last.tap, ref: `UPI${randomSuffix(8)}` }); else void issue(last.tap, "cash", ""); }
        break;
      case "reprintLast":
        if (lastSynced?.receiptId) setReprint({ receiptId: lastSynced.receiptId, local: lastSynced });
        else toast("The last ticket has not reached the server yet; reprint after it syncs.", "danger");
        break;
      case "history": navigate("/history"); break;
      case "shiftSummary": setShiftOpen(true); break;
      case "fullMode": navigate("/sell"); break;
      case "customerDisplay": openCustomerDisplay(); break;
      case "lock": s.lock(); break;
    }
  };

  return (
    <Shell keys={FAST_KEYS} onKey={onKey}>
      <div className="pos-fast">
        <section className="pos-left" aria-label="Presets">
          <CashAlert drawer={drawer} />
          <div className="tos-row">
            <div className="pos-modes" role="radiogroup" aria-label="Payment mode" style={{ minWidth: 320 }}>
              <button type="button" role="radio" className="pos-mode" aria-checked={mode === "cash"} onClick={() => setMode("cash")}>Cash<kbd>F6</kbd></button>
              <button type="button" role="radio" className="pos-mode" aria-checked={mode === "upi"} onClick={() => setMode("upi")}>UPI QR<kbd>F6</kbd></button>
            </div>
            <span className="tos-muted">Type a number first to issue that many tickets in one touch.</span>
          </div>
          {!presets.length ? (
            <Alert tone="warning">
              No ticket presets are set up for this counter.{" "}
              <Button size="sm" onClick={() => void s.reloadConfig()}>Reload counter items</Button>
            </Alert>
          ) : (
            <div className="pos-presets">
              {presets.map((p, i) => (
                <button key={`${p.item.id}-${i}`} type="button" className="pos-preset" style={{ "--tile": p.item.color ?? undefined } as CSSProperties}
                  aria-disabled={busy || undefined} onClick={() => { if (!busy) press(i); }}
                  aria-label={`${p.label}, ${p.item.price ? formatINR(p.item.price) : "free"}, ${Math.max(1, Number(mult || "1")) * p.qty} ticket(s), ${mode}`}>
                  <span className="pos-preset-label">{p.label}</span>
                  <span className="pos-preset-sub"><span>{p.item.price ? formatINR(p.item.price, { decimals: false }) : "Free"}{p.qty > 1 ? ` × ${p.qty}` : ""}</span><kbd>F{i + 1}</kbd></span>
                </button>
              ))}
            </div>
          )}
          {last && (
            <div className="tos-card tos-card-body tos-row" role="status">
              <strong>Last:</strong> {last.sale.number ?? `Roll #${String(last.sale.seq).padStart(5, "0")}`} · {last.tap.qty} × {last.tap.item.name} · {formatINR(last.sale.total)}
              <Badge tone={last.sale.status === "synced" ? "success" : "warning"}>{last.sale.status === "synced" ? "On server" : "Offline, will sync"}</Badge>
              <span className="tos-spacer" />
              <Button onClick={() => onKey("repeatLast")}>Repeat (F5)</Button>
              <Button onClick={() => onKey("reprintLast")}>Reprint (F7)</Button>
            </div>
          )}
        </section>
        <aside className="pos-right" aria-label="Live tally" style={{ padding: 16, gap: 14 }}>
          <div>
            <div className="tos-stat-label">Tickets issued this shift</div>
            <div className="pos-big-count" aria-live="polite">{t.tickets}</div>
          </div>
          <div className="tos-grid-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <StatCard label="Bills" value={t.bills} />
            <StatCard label="Roll serial" value={sales.length ? `#${String(sales[sales.length - 1]!.seq).padStart(5, "0")}` : "—"} />
            <StatCard label="Cash" value={formatINR(t.cash, { decimals: false })} tone="success" />
            <StatCard label="UPI" value={formatINR(t.upi, { decimals: false })} tone="info" />
          </div>
          <div className="pos-mult" aria-live="polite" aria-label="Tickets per touch">× {mult || "1"}</div>
          <div className="pos-keypad" role="group" aria-label="Quantity keypad">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"].map((k) => (
              <button key={k} type="button" aria-label={k === "C" ? "Clear quantity" : k === "⌫" ? "Delete digit" : k}
                onClick={() => setMult((m) => (k === "C" ? "" : k === "⌫" ? m.slice(0, -1) : (m + k).replace(/^0+/, "").slice(0, 3)))}>{k}</button>
            ))}
          </div>
        </aside>
      </div>

      <Modal open={Boolean(upiTap)} title="Collect by UPI" onClose={() => setUpiTap(null)}
        footer={<><Button onClick={() => setUpiTap(null)}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={() => { const u = upiTap; setUpiTap(null); if (u) void issue(u.tap, "upi", u.ref); }}>Payment received, print</Button></>}>
        {upiTap && (
          <div className="tos-stack" style={{ alignItems: "center", textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{upiTap.tap.qty} × {upiTap.tap.item.name}</div>
            <div className="pos-total" style={{ gap: 12 }}><strong>{formatINR(upiAmount)}</strong></div>
            {upi && <QrImage value={upi} alt={`UPI QR code for ${formatINR(upiAmount)}`} size={260} />}
            {upiErr && <Alert tone="warning">{upiErr}</Alert>}
            <div className="tos-muted">Ref {upiTap.ref}. Print only after the UPI success sound.</div>
          </div>
        )}
      </Modal>
      <ReprintModal target={reprint} onClose={() => setReprint(null)} />
      <ShiftDrawer open={shiftOpen} onClose={() => setShiftOpen(false)} />
    </Shell>
  );
}
